import _ from 'lodash';
import { CountAndAmount, SerializedStorage as ZReport } from '@baemingo/report-events/dist/types';
import { Languages, TranslatedString, i18nProps } from '~Types';
import { aggregateCategorySales } from '~utilities';
import { GetVenue_venue_company_staffs } from '../__queries__';
import { displayAggregatedCategoriesInPrint, getCategoryName } from '../utils';
import { CashRegisterTotals } from '~Hooks';

type Data = {
  reportTitle: string;
  venueName: string;
  venueLocation: any;
  venueOrg: string;
  companyInfo: any;
  taxComplianceDeviceName: string;
  reportDate: string;
  data: ZReport;
  cashRegister: CashRegisterTotals | null;
  i18n: i18nProps;
  formatCurrency: (amount: number, divide?: boolean) => string;
  categories: {
    id: string;
    nameLang: TranslatedString;
    parentId?: string;
  }[];
  staffs: GetVenue_venue_company_staffs[];
  payouts: any;
  openCheques: CountAndAmount | null;
};

const LANGUAGE_TO_COUNTRY = {
  en: 'GB',
  se: 'SE',
  es: 'ES',
  no: 'NO',
  dk: 'DK',
  tr: 'TR',
};

// The files are separated due to the different countries that have different requirements for the ZReport.
//Conditionals in the printed code create spaces if the data should not be displayed.
const printGeneralZReport = ({
  reportTitle,
  venueName,
  venueLocation,
  venueOrg,
  companyInfo,
  taxComplianceDeviceName,
  reportDate,
  data,
  cashRegister,
  i18n,
  formatCurrency,
  categories,
  staffs,
  payouts,
  openCheques,
}: Data) => {
  const aggregatedCategories = aggregateCategorySales(categories, data.categoryTotals, i18n);

  const country =
    companyInfo?.country ?? companyInfo?.defaultLanguage
      ? LANGUAGE_TO_COUNTRY[companyInfo.defaultLanguage as Languages]
      : 'SE';

  const swedishCategoriesToDisplay = _.map(
    data.categoryTotals,
    (category, categoryId) =>
      `${category.count}${i18n('ZReport.x')} ${getCategoryName(
        categoryId,
        categories,
        i18n,
      )} ${formatCurrency(category.amount)}`,
  ).join('\n');

  const categoriesToDisplay =
    country === 'SE'
      ? swedishCategoriesToDisplay
      : displayAggregatedCategoriesInPrint(aggregatedCategories, 0, formatCurrency, i18n);

  const paymentMethods = _.map(
    data.paymentMethodTotals,
    (method, name) =>
      `${name} ${method.count}${i18n('ZReport.x')} ${formatCurrency(method.amount)}`,
  ).join('\n');

  const reportToPrint = `
             ${reportTitle}
  ------------------------------------
  ${i18n('ZReport.Created')}: ${reportDate}

  ${i18n('ZReport.SequenceId')}: ${data.sequenceId}
  ${i18n('ZReport.RegisterId')}: ${taxComplianceDeviceName}

  ${venueName}
  ${venueLocation?.street}
  ${`${venueLocation?.zip} ${venueLocation?.city}`}

${i18n('ZReport.SalesPerCategory')}
${categoriesToDisplay}

${i18n('ZReport.TotalSoldArticles')}
${data.numberOfItems}

${i18n('ZReport.TotalRefundedArticles')}
${data.numberOfItemsRefunded}

${i18n('ZReport.TotalSales')}
${data.numberOfOrders}${i18n('ZReport.x')} ${formatCurrency(data.totalSales)}

${i18n('ZReport.VatSpecification')}
${_.map(data.vatTotals, (amount, name) => `${name}%    ${formatCurrency(amount)}`).join('\n')}

${i18n('ZReport.TotalVat')} 
${formatCurrency(data.vatTotalAmount)}

${i18n('ZReport.NetSales')}
${_.map(
  data.saleForVatTotals,
  (amount: number, name: number) => `${name}%    ${formatCurrency(amount - data.vatTotals[name])}`,
).join('\n')}

${i18n('ZReport.Receipts')}
${i18n('ZReport.RegisterReceipts')} ${data.printedReceipts ?? 0}${i18n('ZReport.x')}
${i18n('ZReport.ReceiptCopies')} ${data.printedReceiptCopies + data.emailedReceiptCopies}${i18n(
    'ZReport.x',
  )} ${formatCurrency(data.totalReceiptCopiesAmount)}
${i18n('ZReport.ProvisionalReceipts')} ${data.provisionalReceipts.count}${i18n(
    'ZReport.x',
  )} ${formatCurrency(data.provisionalReceipts.amount)}
${i18n('ZReport.RefundReceipts')} ${data.refundReceipts.count}${i18n(
    'ZReport.x',
  )} ${formatCurrency(data.refundReceipts.amount)}
  
${i18n('ZReport.PaymentMethods')}
${paymentMethods}

${i18n('ZReport.Tips')}
${data.numberOfTips}${i18n('ZReport.x')} ${formatCurrency(data.totalTips)}

${i18n('ZReport.TipRefunds')}
${data.tipRefunds.count}${i18n('ZReport.x')} ${formatCurrency(data.tipRefunds.amount)}

${i18n('ZReport.Refunds')}
${data.totalRefunds?.count ?? 0}${i18n('ZReport.x')} ${formatCurrency(
    data.totalRefunds?.amount ?? 0,
  )}

${i18n('ZReport.Discounts')}
${data.discountsTotal}${i18n('ZReport.x')} ${formatCurrency(data.discountsAmount)}
${
  openCheques
    ? `\n${i18n('Report.OpenCheques')}
${openCheques.count}${i18n('ZReport.x')} ${formatCurrency(openCheques.amount)}\n`
    : ''
}
${i18n('Report.Expenses')}
${formatCurrency(data.totalExpenses)}
${
  cashRegister
    ? `\n${i18n('ZReport.CashRegisterIn')} ${formatCurrency(cashRegister.cashInitial)}
${i18n('ZReport.CashRegisterOut')} ${formatCurrency(cashRegister.cashFinal)}
`
    : ' '
}
${i18n('CashManagement.Payouts')}
${
  Object.keys(payouts)?.length > 0
    ? Object.keys(payouts)
        .map((payout) => {
          const payoutName = payout === 'DRAWER' ? i18n('CashManagement.CashRegister') : payout;
          return `${payoutName} ${formatCurrency(-payouts[payout])}`;
        })
        .join('\n')
    : `${formatCurrency(0)}`
}

${i18n('ZReport.Statistics')}
${i18n('ZReport.Corrections')} ${data.lineCorrections['REMOVE_ITEM']?.count ?? 0}${i18n(
    'ZReport.x',
  )} ${formatCurrency(data.lineCorrections['REMOVE_ITEM']?.amount ?? 0)}
${i18n('ZReport.NonCompletedCheques')} ${data.lineCorrections['REMOVE_CHEQUE']?.count ?? 0}${i18n(
    'ZReport.x',
  )} ${formatCurrency(data.lineCorrections['REMOVE_CHEQUE']?.amount ?? 0)}
${i18n('ZReport.CashDrawerOpenings')} ${data.totalCashBoxOpenings}${i18n('ZReport.x')}

${
  // Spain: hide all-time/grand sales totals — owners don't want staff to see cumulative sales
  country !== 'ES'
    ? `${i18n('ZReport.Sum')}

${i18n('ZReport.GrandTotal')}:
${formatCurrency(data.allTimeSales)}

${i18n('ZReport.GrandTotalRefunds')}:
${formatCurrency(data.allTimeRefunds)}

${i18n('ZReport.GrandTotalNetSales')}:
${formatCurrency(data.allTimeSales - data.allTimeVat)}

${i18n('ZReport.GrandTotalNetSalesSpecial')}:
${formatCurrency(data.allTimeSales - data.allTimeRefunds)}
`
    : ''
}
${companyInfo?.name} ${i18n('ZReport.OrgNr')}: ${venueOrg}
`;
  console.log(reportToPrint);

  window.printerManager?.initiatePrint({
    data: reportToPrint,
    printType: 'Z_REPORT',
  });
};

export default printGeneralZReport;
